import React, { useEffect, useState } from "react"
import { useSelector } from "react-redux"
import { Row, Col } from "react-bootstrap"

import {
    IEC_61508_2010,
    IEC_61508_1998,
    ISA_TR84_2002,
    ISA_TR84_2022,
    VDI_VDE_2180_2019,

    LOW_DEMAND_MODE,
    HIGH_DEMAND_MODE,
    CONTINUOUS_MODE,
} from "const"
import {
    StoreType,
    SilSafetyFunctionFilters,
} from "client/store"
import {
    FormGroup,
    Label,
    Input,
    SelectDropdown,
    SelectDropdownItem,
    CheckboxWrapper,
    CheckButton,
    Empty,
    Button,
    SearchBar,
    SearchFilter,
} from "client/components"

interface SearchSilSafetyFunctionForm {
    filters: SilSafetyFunctionFilters
    onFilter: (filters: SilSafetyFunctionFilters) => void
}
interface SearchSilSafetyFunction extends SearchSilSafetyFunctionForm {
    id: string
    leftOffset?: boolean
    rightOffset?: boolean
}

const SilSafetyFunctionSearch = ({
    id,
    leftOffset = false,
    rightOffset = false,
    filters,
    onFilter,
}: SearchSilSafetyFunction) => {
    const lang = useSelector((state: StoreType) => state.system.lang)

    const popupFilters: SearchFilter[] = []
    const extendedFilters: SearchFilter[] = []

    // popup filters
    popupFilters.push(
        {
            title: lang.calculated,
            isActive: filters.calculated === true,
            onAdd: (obj: SilSafetyFunctionFilters) => ({
                ...obj,
                calculated: true,
            }),
            onClose: (obj: SilSafetyFunctionFilters) => ({
                ...obj,
                calculated: null,
            }),
        },
        {
            title: lang.notCalculated,
            isActive: filters.calculated === false,
            onAdd: (obj: SilSafetyFunctionFilters) => ({
                ...obj,
                calculated: false,
            }),
            onClose: (obj: SilSafetyFunctionFilters) => ({
                ...obj,
                calculated: null,
            }),
        },
    )

    // extended filters
    if (filters.name && filters.name.length) {
        extendedFilters.push({
            title: `${lang.name}: ${filters.name}`,
            onClose: (obj: SilSafetyFunctionFilters) => ({
                ...obj,
                name: null,
            }),
        })
    }
    if (filters.calculationMethod) {
        let calculationMethodTitle: string
        if (filters.calculationMethod === IEC_61508_2010) calculationMethodTitle = lang.iec_61508_2010
        if (filters.calculationMethod === IEC_61508_1998) calculationMethodTitle = lang.iec_61508_1998
        if (filters.calculationMethod === ISA_TR84_2002) calculationMethodTitle = lang.isa_tr84_2002
        if (filters.calculationMethod === ISA_TR84_2022) calculationMethodTitle = lang.isa_tr84_2022
        if (filters.calculationMethod === VDI_VDE_2180_2019) calculationMethodTitle = lang.vdi_vde_2180_2019

        extendedFilters.push({
            title: `${lang.calculationMethod}: ${calculationMethodTitle}`,
            onClose: (obj: SilSafetyFunctionFilters) => ({
                ...obj,
                calculationMethod: null,
            }),
        })
    }
    if (filters.mode) {
        let modeTitle: string
        if (filters.mode === LOW_DEMAND_MODE) modeTitle = lang.modeLowDemand
        if (filters.mode === HIGH_DEMAND_MODE) modeTitle = lang.modeHighDemand
        if (filters.mode === CONTINUOUS_MODE) modeTitle = lang.modeContinuous

        extendedFilters.push({
            title: `${lang.mode}: ${modeTitle}`,
            onClose: (obj: SilSafetyFunctionFilters) => ({
                ...obj,
                mode: null,
            }),
        })
    }

    return (
        <SearchBar
            id={id}
            filters={filters}
            leftOffset={leftOffset}
            rightOffset={rightOffset}
            popupFilters={popupFilters}
            extendedFilters={extendedFilters}
            filterForm={<SilSafetyFunctionFilterForm filters={filters} onFilter={onFilter} />}
            onFilter={onFilter}
        />
    )
}
export default SilSafetyFunctionSearch


const SilSafetyFunctionFilterForm = ({
    filters,
    onFilter,
}: SearchSilSafetyFunctionForm) => {
    const lang = useSelector((state: StoreType) => state.system.lang)

    const [name, setName] = useState(filters.name || "")
    const [calculationMethod, setCalculationMethod] = useState(filters.calculationMethod)
    const [mode, setMode] = useState(filters.mode)
    const [calculated, setCalculated] = useState(filters.calculated)

    useEffect(() => {
        setName(filters.name || "")
        setCalculationMethod(filters.calculationMethod)
        setMode(filters.mode)
        setCalculated(filters.calculated)
    }, [filters])

    const onSearch = () => {
        const newFilters = {
            ...filters,
            name,
            calculationMethod,
            mode,
            calculated,
        }
        onFilter(newFilters)
    }

    let calculationMethodTitle: string
    if (calculationMethod === IEC_61508_2010) calculationMethodTitle = lang.iec_61508_2010
    if (calculationMethod === IEC_61508_1998) calculationMethodTitle = lang.iec_61508_1998
    if (calculationMethod === ISA_TR84_2002) calculationMethodTitle = lang.isa_tr84_2002
    if (calculationMethod === ISA_TR84_2022) calculationMethodTitle = lang.isa_tr84_2022
    if (calculationMethod === VDI_VDE_2180_2019) calculationMethodTitle = lang.vdi_vde_2180_2019

    let calculatedTitle: string
    if (calculated === true) calculatedTitle = lang.calculated
    if (calculated === false) calculatedTitle = lang.notCalculated

    return (
        <Row>
            <Col xs={12}>
                <FormGroup>
                    <Label>{lang.name}</Label>
                    <Input type="text" value={name} onChange={(e) => setName(e.target.value)} />
                </FormGroup>
                <FormGroup>
                    <Label>{lang.calculationMethod}</Label>
                    <SelectDropdown title={calculationMethodTitle || <Empty />}>
                        <SelectDropdownItem onClick={() => setCalculationMethod(null)}><Empty /></SelectDropdownItem>
                        <SelectDropdownItem onClick={() => setCalculationMethod(IEC_61508_2010)}>{lang.iec_61508_2010}</SelectDropdownItem>
                        <SelectDropdownItem onClick={() => setCalculationMethod(IEC_61508_1998)}>{lang.iec_61508_1998}</SelectDropdownItem>
                        <SelectDropdownItem onClick={() => setCalculationMethod(VDI_VDE_2180_2019)}>
                            {lang.vdi_vde_2180_2019}
                        </SelectDropdownItem>
                        {
                            // TODO: temporarily disabled
                            // <SelectDropdownItem onClick={() => setCalculationMethod(ISA_TR84_2002)}>
                            //     {lang.isa_tr84_2002}
                            // </SelectDropdownItem>
                        }
                        <SelectDropdownItem onClick={() => setCalculationMethod(ISA_TR84_2022)}>{lang.isa_tr84_2022}</SelectDropdownItem>
                    </SelectDropdown>
                </FormGroup>
                <FormGroup>
                    <Label>{lang.mode}</Label>
                    <CheckboxWrapper>
                        <Col xs={12} md={4}>
                            <CheckButton
                                checked={mode === LOW_DEMAND_MODE}
                                onChange={() => setMode(mode === LOW_DEMAND_MODE ? null : LOW_DEMAND_MODE)}
                                title={lang.modeLowDemand}
                            />
                        </Col>
                        <Col xs={12} md={4}>
                            <CheckButton
                                checked={mode === HIGH_DEMAND_MODE}
                                onChange={() => setMode(mode === HIGH_DEMAND_MODE ? null : HIGH_DEMAND_MODE)}
                                title={lang.modeHighDemand}
                            />
                        </Col>
                        <Col xs={12} md={4}>
                            <CheckButton
                                checked={mode === CONTINUOUS_MODE}
                                onChange={() => setMode(mode === CONTINUOUS_MODE ? null : CONTINUOUS_MODE)}
                                title={lang.modeContinuous}
                            />
                        </Col>
                    </CheckboxWrapper>
                </FormGroup>
                <FormGroup>
                    <Label>{lang.calculated}</Label>
                    <SelectDropdown title={calculatedTitle || <Empty />}>
                        <SelectDropdownItem onClick={() => setCalculated(null)}><Empty /></SelectDropdownItem>
                        <SelectDropdownItem onClick={() => setCalculated(true)}>{lang.calculated}</SelectDropdownItem>
                        <SelectDropdownItem onClick={() => setCalculated(false)}>{lang.notCalculated}</SelectDropdownItem>
                    </SelectDropdown>
                </FormGroup>
            </Col>
            <Col xs={12}>
                <Button row onClick={onSearch}>{lang.search}</Button>
            </Col>
        </Row>
    )
}
